import { useApi } from "@/hooks/use-api";
import { useAppState } from "@/store/useApp";
import { DocumentInfo } from "@/types/document";
import { useEffect, useRef } from "react";
import { CheckCircleIcon, Dot, Mic } from "lucide-react";
import { DocumentBadge } from "./DocumentBadge";
import { DocumentActions } from "./DocumentActions";
import { config } from "@/config";

interface DocumentItemProps {
  doc: DocumentInfo;
  loadingDocId: string | null;
  onclick: (doc: DocumentInfo) => void;
  isFocused?: boolean;
  tabIndex?: number | null;
}

export const DocumentItem = ({
  doc,
  loadingDocId,
  onclick,
  isFocused = false,
  tabIndex,
}: DocumentItemProps) => {
  const assessments = useAppState((state) => state.assessments || []);
  const courses = useAppState((state) => state.courses || []);
  const { updateDocument } = useApi();
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFocused && itemRef.current && tabIndex !== null) {
      itemRef.current.focus();
      itemRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isFocused, doc.id, tabIndex]);

  const getAssessmentInfo = () => {
    if (!doc.assessmentId) return null;
    const assessment = assessments.find((a) => a.id === doc.assessmentId);
    if (!assessment) return null;
    const course = courses.find((c) => c.id === assessment.courseId);
    return { assessment, course };
  };

  const handleFlagChange = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (doc.status === "flagged") {
      const status = doc.grade ? "done" : "ready";
      await updateDocument(doc.id, { status });
    } else {
      await updateDocument(doc.id, { status: "flagged" });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && doc.status !== "importing" && !isLoading) {
      onclick(doc);
    }
  };

  const assessmentInfo = getAssessmentInfo();
  const isLoading = loadingDocId === doc.id;

  const getCursorClass = () => {
    if (isLoading) return "cursor-wait";
    if (doc.status === "importing") return "cursor-not-allowed";
    return "cursor-pointer";
  };

  const id = doc.metaType === "onboarding" ? "demo-submission" : doc.id;

  return (
    <div
      ref={itemRef}
      key={doc.id}
      id={id}
      data-test-role="document-item"
      data-testid={id}
      className={`rounded-md w-full border shadow-xs bg-white outline-none focus:border-brand focus-visible:border-brand hover:shadow-sm transition-shadow ${getCursorClass()}`}
      onClick={() =>
        doc.status !== "importing" &&
        doc.status !== "failed" &&
        !isLoading &&
        onclick(doc)
      }
      onKeyDown={handleKeyDown}
      tabIndex={tabIndex ?? undefined}
    >
      <div className="flex items-center p-4 h-full">
        <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded mr-4">
          {doc.fileType ? (
            <span className="text-sm font-medium uppercase">{doc.fileType}</span>
          ) : (
            <Mic className="w-5 h-5 text-gray-500" />
          )}
        </div>

        <div className="flex flex-col flex-1">
          <div className="flex items-center gap-1">
            <span className="text-sm font-medium leading-normal" role="text" data-test-role="document-title">
              {doc.visibleStudentId && `${doc.visibleStudentId} - `}{doc.title}
            </span>
            {doc.status === "done" && (
              <CheckCircleIcon className="w-4 h-4 text-green-600" />
            )}
            {doc.validation &&
              doc.validation.passed === false &&
              !config.isProduction &&
              doc.status !== "importing" && (
                <Dot className="w-6 h-6 ml-[-4px] text-orange-500 fill-current" />
              )}
          </div>
          <div className="flex flex-col gap-0.5 mt-0.5">
            {assessmentInfo && (
              <div className="text-xs text-gray-400">
                {assessmentInfo.course?.reference} -{" "}
                {assessmentInfo.assessment.title}
              </div>
            )}
          </div>
        </div>

        <div className="ml-auto self-start flex items-center gap-2">
          <DocumentBadge
            status={doc.status}
            progress={doc.progress}
            grade={doc.grade}
            onClick={doc.status === "flagged" ? handleFlagChange : undefined}
          />
          <DocumentActions doc={doc} onFlagChange={handleFlagChange} />
        </div>
      </div>
    </div>
  );
};
